import React, { useEffect, useState } from 'react';
import Dropdown from '../../components/Dropdown';
import { useTranslation } from '../../contexts/TranslationContext';
import { apiGet, apiPatch } from '../../utils/api';
import '../../styles/leagues.scss';

interface LeagueItem {
  id: number;
  name: string;
  sport_id: number;
  sport_name: string;
  country_code: string;
  download: boolean;
}

interface PaginatedLeaguesResponse {
  leagues: LeagueItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface SportItem {
  id: number;
  name: string;
  icon: string | null;
}

const Leagues: React.FC = () => {
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState<PaginatedLeaguesResponse | null>(null);
  const [sports, setSports] = useState<SportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sportFilter, setSportFilter] = useState('all');
  const [downloadFilter, setDownloadFilter] = useState('all');

  useEffect(() => {
    apiGet<SportItem[]>('/admin/sports').then(setSports).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchLeagues = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          page_size: '50',
        });
        if (sportFilter !== 'all') params.set('sport_id', sportFilter);
        if (downloadFilter !== 'all') params.set('download', downloadFilter);

        const data = await apiGet<PaginatedLeaguesResponse>(`/admin/leagues?${params.toString()}`);
        setLeagues(data);
      } catch (err) {
        console.error('Failed to fetch leagues:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, [currentPage, sportFilter, downloadFilter]);

  const handleToggleDownload = async (league: LeagueItem) => {
    setSavingId(league.id);
    try {
      const updated = await apiPatch<{ id: number; download: boolean }>(
        `/admin/leagues/${league.id}/download`,
        { download: !league.download }
      );
      setLeagues(prev =>
        prev
          ? {
              ...prev,
              leagues: prev.leagues.map(l =>
                l.id === updated.id ? { ...l, download: updated.download } : l
              ),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to update league download flag:', err);
    } finally {
      setSavingId(null);
    }
  };

  const sportOptions = [
    { value: 'all', label: t.filters.all },
    ...sports.map(s => ({ value: String(s.id), label: s.name, icon: s.icon })),
  ];

  const downloadOptions = [
    { value: 'all', label: t.filters.all },
    { value: 'true', label: t.admin.downloading },
    { value: 'false', label: t.admin.notDownloading },
  ];

  return (
    <div className="leagues">
      <div className="leagues__container">
        <h1 className="leagues__title">{t.admin.leagues}</h1>

        <div className="leagues__filters">
          <div className="leagues__filter-field">
            <label className="leagues__filter-label" htmlFor="leagues-sport-filter">
              {t.admin.sport}
            </label>
            <Dropdown
              id="leagues-sport-filter"
              value={sportFilter}
              onChange={(value) => { setSportFilter(value); setCurrentPage(1); }}
              options={sportOptions}
              title={t.admin.sport}
              placeholder={t.filters.all}
            />
          </div>
          <div className="leagues__filter-field">
            <label className="leagues__filter-label" htmlFor="leagues-download-filter">
              {t.admin.download}
            </label>
            <Dropdown
              id="leagues-download-filter"
              value={downloadFilter}
              onChange={(value) => { setDownloadFilter(value); setCurrentPage(1); }}
              options={downloadOptions}
              title={t.admin.download}
              placeholder={t.filters.all}
            />
          </div>
        </div>

        {loading ? (
          <p>{t.common.loading}</p>
        ) : leagues && (
          <>
            <div className="leagues__list">
              <div className="leagues__table">
                <div className="leagues__header">
                  <div className="leagues__header-cell">{t.admin.league}</div>
                  <div className="leagues__header-cell">{t.admin.sport}</div>
                  <div className="leagues__header-cell">{t.admin.country}</div>
                  <div className="leagues__header-cell">{t.admin.download}</div>
                </div>

                {leagues.leagues.map((league) => (
                  <div key={league.id} className="leagues__row">
                    <div className="leagues__cell">{league.name}</div>
                    <div className="leagues__cell">{league.sport_name}</div>
                    <div className="leagues__cell">{league.country_code}</div>
                    <div className="leagues__cell">
                      <label className="leagues__toggle">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={league.download}
                          disabled={savingId === league.id}
                          onChange={() => handleToggleDownload(league)}
                        />
                        <span className="leagues__toggle-track" aria-hidden />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {leagues.total_pages > 1 && (
              <div className="leagues__pagination">
                <button
                  className="button_secondary leagues__pagination-button"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t.common.previous}
                </button>
                <span className="leagues__pagination-info">
                  {t.common.paginationShort
                    .replace('{page}', String(currentPage))
                    .replace('{totalPages}', String(leagues.total_pages))}{' '}
                  {t.common.totalCount.replace('{total}', String(leagues.total))}
                </span>
                <button
                  className="button_secondary leagues__pagination-button"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === leagues.total_pages}
                >
                  {t.common.next}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Leagues;
